import React, { useState } from 'react';
import PageShell from '../../components/PageShell';
import { Eyebrow, SectionHeading, GradientButton, StepChip } from '../../components/common/Primitives';
import LicenceFeeHighlight from '../../components/common/LicenceFeeHighlight';
import { EXPLORE_ACCESS } from '../../lib/mockExplore';
import { submitForm } from '../../lib/api';
import { BRAND } from '../../lib/mock';
import { Link } from 'react-router-dom';
import { CheckCircle2, ArrowRight } from 'lucide-react';

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-[12.5px] text-ink-500">{label}{required && <span className="text-purple"> *</span>}</span>
      {children}
    </label>
  );
}

const Radio = ({ options, value, onChange, testPrefix }) => (
  <div className="mt-2 flex flex-wrap gap-2">
    {options.map(o => (
      <button
        type="button"
        key={o}
        onClick={() => onChange(o)}
        data-testid={testPrefix ? `${testPrefix}-${o.replace(/\s+/g,'-').toLowerCase()}` : undefined}
        className={`rounded-lg border px-3 py-2 text-[13px] font-semibold transition-colors ${value === o ? 'border-purple bg-purple-50 text-purple' : 'border-canvas-200 bg-white text-navy hover:border-purple'}`}
      >{o}</button>
    ))}
  </div>
);

const inputCls = 'mt-1 w-full rounded-lg border border-canvas-200 px-3 py-2.5 text-[13.5px] focus:border-purple';

export default function ExploreAccessRequest() {
  const d = EXPLORE_ACCESS;
  const [v, setV] = useState({ role: 'Student', grade: 'Grade 8', interest: 'Science', quest: 'No', lang: 'English', agree: true, agreeCost: false });
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const on = (k) => (e) => setV(s => ({ ...s, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true); setSubmitError(null);
    try {
      await submitForm({
        formType: 'explore-access',
        fields: v,
        contactEmail: v.email,
        contactName: v.name,
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSubmitError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell>
      {/* HERO */}
      <section className="px-6 pt-12 pb-8" data-testid="explore-access-hero">
        <div className="mx-auto max-w-[1240px]">
          <Eyebrow color="purple">{d.hero.eyebrow}</Eyebrow>
          <div className="mt-3 grid grid-cols-1 gap-10 lg:grid-cols-[1.2fr_1fr]">
            <div>
              <h1 className="text-[40px] md:text-[52px] font-extrabold leading-[1.05] tracking-tight text-navy">{d.hero.title}</h1>
              <p className="mt-5 max-w-2xl text-[15.5px] leading-relaxed text-ink-500">{d.hero.desc}</p>
              <div className="mt-6"><LicenceFeeHighlight /></div>
            </div>
            <div className="rounded-3xl border border-mint-50 bg-mint-50/40 p-6 shadow-soft">
              <div className="text-[11.5px] font-bold tracking-widest text-mint-600">SOFTWARE ACCESS</div>
              <div className="mt-2 text-[16px] font-bold text-navy">{d.hero.softwareCard.title}</div>
              {d.hero.softwareCard.lines.map((l, i) => (<p key={i} className="mt-2 text-[13px] text-ink-700">{l}</p>))}
            </div>
          </div>
        </div>
      </section>

      {done ? (
        <section className="px-6 pb-24" data-testid="explore-access-confirmation">
          <div className="mx-auto max-w-[1100px]">
            <div className="rounded-3xl bg-brand-gradient p-8 md:p-10 text-white shadow-card">
              <div className="flex items-center gap-3"><CheckCircle2 className="h-8 w-8" /><h3 className="text-[22px] font-extrabold">Request received</h3></div>
              <p className="mt-3 max-w-2xl text-[14px] text-white/85">Thank you. The Gyan Dhara team will review your request and respond within 48 hours.</p>
              <div className="mt-6 text-[12.5px] text-white/70">This preview stores your submission locally. Once email is enabled it will be sent to {BRAND.contactEmail}.</div>
              <Link to="/explore" className="mt-6 inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-[13px] font-semibold text-navy">Return to Explore <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </div>
        </section>
      ) : (
        <section className="px-6 pb-16">
          <div className="mx-auto max-w-[1100px]">
            <form onSubmit={submit} className="rounded-2xl border border-canvas-200 bg-white p-6 md:p-8 shadow-soft" data-testid="explore-access-form">
              <div className="text-[15px] font-bold text-navy">{d.form.title}</div>
              <div className="text-[12px] text-ink-500">{d.form.hint}</div>

              <div className="mt-6">
                <Field label="I am a" required>
                  <Radio options={d.form.roles} value={v.role} onChange={on('role')} testPrefix="ea-role" />
                </Field>
              </div>

              <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2">
                <Field label="Full name" required><input required data-testid="ea-name" onChange={on('name')} placeholder="Enter full name" className={inputCls} /></Field>
                <Field label="Organisation or school"><input data-testid="ea-org" onChange={on('org')} placeholder="Optional" className={inputCls} /></Field>
                <Field label="Email" required><input required type="email" data-testid="ea-email" onChange={on('email')} placeholder="name@example.com" className={inputCls} /></Field>
                <Field label="Phone" required><input required data-testid="ea-phone" onChange={on('phone')} placeholder="Enter phone number" className={inputCls} /></Field>
                <Field label="Learner age"><input data-testid="ea-age" onChange={on('age')} placeholder="Enter learner age" className={inputCls} /></Field>
                <Field label="City" required><input required data-testid="ea-city" onChange={on('city')} placeholder="Enter city" className={inputCls} /></Field>
              </div>

              <div className="mt-6"><Field label="Student grade" required>
                <Radio options={d.form.grades} value={v.grade} onChange={on('grade')} testPrefix="ea-grade" />
              </Field></div>

              <div className="mt-6"><Field label="Primary learning interest" required>
                <Radio options={d.form.interests} value={v.interest} onChange={on('interest')} testPrefix="ea-interest" />
              </Field></div>

              <div className="mt-6"><Field label="Do you already have a Meta Quest 3S?" required>
                <Radio options={d.form.quest} value={v.quest} onChange={on('quest')} testPrefix="ea-quest" />
              </Field></div>

              <div className="mt-6"><Field label="Preferred language">
                <Radio options={d.form.langs} value={v.lang} onChange={on('lang')} testPrefix="ea-lang" />
              </Field></div>

              <div className="mt-6"><Field label="What would you like to understand before starting?">
                <textarea rows={4} data-testid="ea-message" onChange={on('message')} placeholder="Share your questions about learning, hardware or access." className={inputCls} />
              </Field></div>

              <div className="mt-6 space-y-3">
                <label className="flex items-start gap-2 text-[13px] text-ink-700"><input type="checkbox" checked={v.agree} onChange={e => setV(s => ({ ...s, agree: e.target.checked }))} className="mt-0.5 accent-purple" /> I agree to be contacted about Gyan Dhara access and setup.</label>
                <label className="flex items-start gap-2 text-[13px] text-ink-700"><input type="checkbox" checked={v.agreeCost} onChange={e => setV(s => ({ ...s, agreeCost: e.target.checked }))} className="mt-0.5 accent-purple" /> I understand that software access and hardware or logistics costs are separate.</label>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-4">
                <button type="submit" disabled={submitting} data-testid="ea-submit" className={`inline-flex items-center gap-1 rounded-lg px-5 py-2.5 text-[13.5px] font-semibold text-white shadow-soft ${submitting ? 'bg-canvas-200 text-ink-500 cursor-not-allowed' : 'bg-brand-gradient hover:brightness-110'}`}>{submitting ? 'Submitting…' : d.form.submit} <ArrowRight className="h-4 w-4" /></button>
                <span className="text-[12.5px] text-ink-500">The request will be sent to {BRAND.contactEmail}</span>
              </div>
              {submitError && (
                <div className="mt-3 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-[12.5px] text-red-700" data-testid="ea-submit-error">{submitError}</div>
              )}
              <div className="mt-4 rounded-lg bg-canvas-100 p-3 text-[11.5px] text-ink-500">Privacy note: information is used only to respond to the access request and support the agreed setup pathway.</div>
            </form>
          </div>
        </section>
      )}

      {/* WHAT HAPPENS NEXT */}
      <section className="px-6 py-12 bg-white" data-testid="explore-access-next">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow="WHAT HAPPENS NEXT" title="A predictable, considered process" />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {d.next.map((s, i) => (
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft flex items-start gap-3">
                <StepChip n={s.n} color={['purple','royal','cyan','mint','purple'][i]} />
                <div>
                  <div className="text-[13.5px] font-bold text-navy">{s.title}</div>
                  <div className="mt-1 text-[12px] text-ink-500">{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BEFORE SUBMITTING */}
      <section className="px-6 pb-24 pt-8" data-testid="explore-access-consider">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow="BEFORE SUBMITTING" title="What to consider" desc="You do not need to know the complete setup. The discussion will help clarify it." />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {d.consider.map((s, i) => (
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft flex items-start gap-3">
                <StepChip n={s.n} color={['purple','royal','cyan','mint'][i]} />
                <div>
                  <div className="text-[13.5px] font-bold text-navy">{s.title}</div>
                  <div className="mt-1 text-[12px] text-ink-500">{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PageShell>
  );
}
